import { notFound } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import * as content from '../lib/content';

export type PageContentId = content.PageContentId;

export type PageContentResult<Key extends PageContentId> = content.PageContentResult<Key>;

export type PageContentResultBySlug<Key extends PageContentId> =
  content.PageContentResultBySlug<Key>;

/*
|------------------
| Page Content (base)
|------------------
*/

const getPageContentSchema = z.object({
  key: z.enum([
    'home',
    'about',
    'contact',
    'pricing',
    'security',
    'careers',
    'customers',
    'ducks',
    'signup',
  ]),
});

const _getPageContent = createServerFn({
  method: 'POST',
})
  .validator((data: unknown) => getPageContentSchema.parse(data))
  .handler(async (ctx) => {
    const { key } = ctx.data;
    const result = content.getServerOnlyPageContent(key);

    if (!result) {
      throw notFound({
        data: { key },
      });
    }

    return result;
  });

/**
 * Get the content for a page.
 *
 * @param data.key The key of the page.
 */
export function getPageContent<Key extends PageContentId>({
  data: { key },
}: { data: { key: Key } }) {
  return _getPageContent({ data: { key } }) as Promise<PageContentResult<Key>>;
}

/*
|------------------
| Page Content (by slug aka many pages)
|------------------
*/

const getPageContentBySlugSchema = z.object({
  key: z.enum(['booking', 'legal', 'signup']),
  slug: z.string(),
});

const _getPageContentBySlug = createServerFn({
  method: 'POST',
})
  .validator((data: unknown) => getPageContentBySlugSchema.parse(data))
  .handler(async (ctx) => {
    const { key, slug } = ctx.data;
    const result = content.getServerOnlyPageContentBySlug<typeof key>(key, slug);

    if (!result) {
      throw notFound({
        data: { key, slug },
      });
    }

    return result;
  });

/**
 * Get the content for a page by slug.
 *
 * @param data.key The key of the page.
 * @param data.slug The slug of the page.
 * @returns The content for the page.
 */
export function getPageContentBySlug<Key extends PageContentId>({
  data: { key, slug },
}: { data: { key: Key; slug: string } }) {
  return _getPageContentBySlug({ data: { key, slug } }) as Promise<PageContentResultBySlug<Key>>;
}
